import {
  IconCounterConversionPath,
  IconCounterRecycling,
  IconCounterStarShine,
} from "assets/svg";
import React from "react";
import {
  CompanyCounter,
  useGetCompanyCounterQuery,
} from "reducers/companyProfileApi";
import {
  CompanyProfileCard,
  CompanyProfileCardTitle,
} from "./CompanyProfileCard";
import { formatWeightWithUnit } from "common/helper/quantity.helper";

interface CompanyAchievementCardProps {
  companyCode?: string;
}

interface AchievementRow {
  label: string;
  value: string;
  Icon: typeof IconCounterRecycling;
}

const hasPositiveValue = (value: number | null | undefined) =>
  value !== null && value !== undefined && value > 0;

const toAchievementRows = (counter: CompanyCounter): AchievementRow[] => [
  {
    label: "総循環量",
    value: hasPositiveValue(counter.total_circulation_kg)
      ? formatWeightWithUnit(counter.total_circulation_kg)
      : "--",
    Icon: IconCounterRecycling,
  },
  {
    label: "継続力",
    value: hasPositiveValue(counter.total_deals)
      ? `${counter.total_deals.toLocaleString()} 回`
      : "--",
    Icon: IconCounterStarShine,
  },
  {
    label: "本日接続数",
    value: hasPositiveValue(counter.today_connections)
      ? counter.today_connections.toLocaleString()
      : "--",
    Icon: IconCounterConversionPath,
  },
];

const AchievementMessage = ({ children }: { children: React.ReactNode }) => (
  <p className="py-[30px] text-center font-['Noto_Sans_JP'] text-[14px] leading-[1.6] text-white/70">
    {children}
  </p>
);

const CompanyAchievementCard = ({
  companyCode,
}: CompanyAchievementCardProps) => {
  const { data, isLoading, isError } = useGetCompanyCounterQuery(
    { companyCode: companyCode ?? "" },
    { skip: !companyCode },
  );

  const renderBody = () => {
    if (isLoading || !companyCode) {
      return <AchievementMessage>読み込み中...</AchievementMessage>;
    }
    if (isError || !data) {
      return (
        <AchievementMessage>成績を取得できませんでした</AchievementMessage>
      );
    }

    return (
      <div>
        {toAchievementRows(data).map(({ label, value, Icon }) => (
          <div
            key={label}
            className="flex min-h-[65px] items-center gap-[15px] border-b border-white/50 py-[15px]"
          >
            <div className="flex min-w-0 flex-1 items-center gap-[5px] text-white">
              <div className="h-[26px] w-[26px] shrink-0 text-[#d4f754]">
                <Icon aria-hidden="true" className="h-full w-full" />
              </div>
              <span className="font-['Noto_Sans_JP'] text-[15px] font-bold leading-[1.6] sm:text-[16px]">
                {label}
              </span>
            </div>
            <span className="shrink-0 text-right font-['Audiowide'] text-[18px] leading-[1.6] text-[#d4f754] sm:text-[22px]">
              {value}
            </span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <CompanyProfileCard className="xl:min-h-[406px]">
      <CompanyProfileCardTitle>成績</CompanyProfileCardTitle>
      {renderBody()}
    </CompanyProfileCard>
  );
};

export default CompanyAchievementCard;
